import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import MoreIcon from "@material-ui/icons/MoreHoriz";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import BoltIcon from "@material-ui/icons/FlashOn";
import EditIcon from "@material-ui/icons/EditOutlined";
import LeftIcon from "@material-ui/icons/ChevronLeft";
import RightIcon from "@material-ui/icons/ChevronRight";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import SettingsIcon from "@material-ui/icons/SettingsOutlined";
import CheckIcon from "@material-ui/icons/Check";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import FunnelIcon from "@material-ui/icons/ViewColumnOutlined";
import { toast } from "react-toastify";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useProject } from "../../context/Project/ProjectContext";
import { useConfirm } from "../../components/ConfirmDialog";
import { useMenuStyles } from "../../components/menuStyles";
import {
  T,
  Chip,
  Dot,
  SelectField,
  money,
  fmtPhone,
  fmtField,
  timeAgo,
  daysSince,
  sourceKind,
  sourceLabel,
  useCrmSocket,
  useDebounced,
  BTN,
  BTN_PRI,
  STAGE_COLORS,
} from "./common";
import ContactDrawer from "./ContactDrawer";
import StageAutomations from "./StageAutomations";
import SourcesDialog from "./SourcesDialog";

const COL_W = 264;

const useStyles = makeStyles((theme) => ({
  page: {
    background: T.bg,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  top: {
    padding: "20px 24px 0",
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  headRow: { display: "flex", alignItems: "center", gap: 12 },
  headIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#EEF0F3",
    color: "#5B6470",
  },
  title: {
    fontFamily: theme.app.heading,
    fontSize: 22,
    fontWeight: 700,
    color: T.ink,
    lineHeight: 1.25,
  },
  actions: { display: "flex", gap: 8, alignItems: "center", flexShrink: 0 },
  tools: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  grow: { flex: 1 },
  sel: {
    minWidth: 170,
    maxWidth: 240,
    "& .MuiInputBase-root": { background: "#fff" },
  },
  search: {
    width: 240,
    "& .MuiInputBase-root": { fontSize: 13, background: "#fff" },
  },
  btn: BTN,
  btnPri: BTN_PRI,
  board: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    gap: 12,
    padding: "16px 24px 20px",
    overflowX: "auto",
    overflowY: "hidden",
    alignItems: "stretch",
  },
  col: {
    width: COL_W,
    flexShrink: 0,
    background: "#EEF0F3",
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    maxHeight: "100%",
  },
  colOver: { outline: `2px dashed ${T.dark}`, outlineOffset: -2 },
  ch: {
    padding: "10px 10px 6px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  chr: { display: "flex", alignItems: "center", gap: 6 },
  chName: {
    fontFamily: theme.app.heading,
    fontSize: 13,
    fontWeight: 600,
    color: T.ink,
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cnt: { fontFamily: T.mono, fontSize: 11, color: T.ink2 },
  sum: { fontSize: 11, color: T.ink3, fontVariantNumeric: "tabular-nums" },
  auto: {
    fontSize: 10.5,
    color: T.ink2,
    background: T.panel,
    border: "1px dashed #C9CED5",
    borderRadius: 7,
    padding: "3px 7px",
    display: "flex",
    gap: 5,
    alignItems: "center",
    cursor: "pointer",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  list: {
    flex: 1,
    minHeight: 60,
    overflowY: "auto",
    padding: "4px 8px 8px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  card: {
    background: T.panel,
    border: `1px solid ${T.line}`,
    borderRadius: 10,
    padding: "9px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 5,
    cursor: "grab",
    userSelect: "none",
    "&:hover": { borderColor: "#C9CED5" },
    "&:focus-visible": { outline: `2px solid ${T.info}` },
  },
  cardDrag: {
    boxShadow: "0 12px 30px rgba(27,34,42,.22)",
    cursor: "grabbing",
    transform: "rotate(1.5deg)",
  },
  nm: {
    fontWeight: 600,
    fontSize: 12.5,
    color: T.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  ph: { fontFamily: T.mono, fontSize: 11, color: T.ink2 },
  chips: { display: "flex", gap: 4, flexWrap: "wrap" },
  kv: {
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
    fontSize: 11,
    color: T.ink2,
  },
  kvv: {
    color: T.ink,
    fontVariantNumeric: "tabular-nums",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "60%",
  },
  ft: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 10.5,
    color: T.ink3,
    gap: 6,
  },
  more: { fontSize: 11, color: T.ink3, textAlign: "center", padding: 4 },
  add: {
    fontSize: 11.5,
    color: T.ink3,
    padding: "4px 6px",
    background: "none",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
    font: "inherit",
    "&:hover": { color: T.ink },
  },
  empty: {
    margin: "auto",
    textAlign: "center",
    color: T.ink3,
    maxWidth: 420,
    fontSize: 13.5,
    lineHeight: 1.6,
  },
  dlg: {
    padding: 22,
    width: 420,
    maxWidth: "92vw",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  dlgTitle: {
    fontFamily: theme.app.heading,
    fontSize: 17,
    fontWeight: 600,
    color: T.ink,
    margin: 0,
  },
  row: { display: "flex", gap: 8, justifyContent: "flex-end" },
  banner: {
    margin: "14px 24px 0",
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: T.panel,
    border: `1px solid ${T.line}`,
    borderRadius: 12,
    padding: "10px 14px",
    fontSize: 13,
    color: T.ink2,
    "& > span": { flex: 1 },
  },
  nameInput: {
    flex: 1,
    minWidth: 0,
    font: "inherit",
    fontFamily: theme.app.heading,
    fontSize: 13,
    fontWeight: 600,
    color: T.ink,
    border: `1px solid ${T.dark}`,
    borderRadius: 6,
    padding: "2px 6px",
    outline: "none",
    background: "#fff",
  },
  chNameEdit: {
    cursor: "text",
    "&:hover": { textDecoration: "underline dotted", textUnderlineOffset: 3 },
  },
  kindTag: {
    fontSize: 9.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".04em",
    color: T.ink3,
  },
  iconBtn: {
    padding: 3,
    color: T.ink3,
    "& svg": { fontSize: 17 },
    "&:hover": { color: T.ink },
  },
  boltOn: { color: "#5f7a12" },
  ghost: {
    width: COL_W,
    flexShrink: 0,
    borderRadius: 12,
    border: "1.5px dashed #C9CED5",
    display: "flex",
    flexDirection: "column",
    padding: 10,
    gap: 8,
    alignSelf: "flex-start",
  },
  ghostBtn: {
    font: "inherit",
    fontSize: 13,
    fontWeight: 600,
    color: T.ink2,
    background: "none",
    border: 0,
    cursor: "pointer",
    padding: "6px 4px",
    textAlign: "left",
    "&:hover": { color: T.ink },
  },
  swRow: { display: "flex", gap: 6, padding: "6px 10px 8px" },
  sw: {
    width: 20,
    height: 20,
    borderRadius: 6,
    border: 0,
    cursor: "pointer",
    padding: 0,
  },
  swOn: { outline: `2px solid ${T.dark}`, outlineOffset: 2 },
  tpl: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    textAlign: "left",
    font: "inherit",
    border: `1px solid ${T.line}`,
    borderRadius: 12,
    padding: "10px 12px",
    background: "#fff",
    cursor: "pointer",
    "&:hover": { borderColor: "#C9CED5" },
  },
  tplOn: { borderColor: T.dark, boxShadow: `0 0 0 1px ${T.dark}` },
  tplName: { fontSize: 13, fontWeight: 600, color: T.ink },
  tplSub: { fontSize: 11.5, color: T.ink3 },
}));

// modelos de funil (as etapas e regras moram no backend — PIPELINE_TEMPLATES)
const TEMPLATES = [
  {
    value: "vendas_ia",
    label: "Vendas com IA no WhatsApp",
    sub: "Novo lead → Em conversa → Qualificado → Proposta. Já anda sozinho: respondeu, IA qualificou, 7 dias parado.",
  },
  {
    value: "vendas",
    label: "Vendas",
    sub: "Novo lead → Em conversa → Qualificado → Proposta → Ganho / Perdido. Você move à mão.",
  },
  {
    value: "atendimento",
    label: "Atendimento",
    sub: "Novo → Em atendimento → Aguardando cliente → Resolvido. Volta sozinho quando o cliente responde.",
  },
  {
    value: "simples",
    label: "Em branco",
    sub: "A fazer → Fazendo → Concluído.",
  },
];

// ---------- card ----------
const CardBody = ({ card, cardFields, valueKey, classes, slaDays }) => {
  const contact = card.contact || {};
  const src = (contact.sources || [])[0];
  const late = slaDays && daysSince(card.enteredStageAt) > slaDays;
  return (
    <>
      <span className={classes.nm}>
        {contact.name ||
          fmtPhone(contact.phone) ||
          contact.email ||
          `#${contact.id}`}
      </span>
      {contact.phone ? (
        <span className={classes.ph}>{fmtPhone(contact.phone)}</span>
      ) : contact.email ? (
        <span className={classes.ph}>{contact.email}</span>
      ) : null}
      <div className={classes.chips}>
        {card.live === "ai" && <Chip kind="ai">● IA conversando</Chip>}
        {card.live === "human" && <Chip kind="human">● Atendente</Chip>}
        {src && <Chip kind={sourceKind(src)}>{sourceLabel(src)}</Chip>}
        {(contact.optOut || {}).whatsapp && <Chip kind="danger">opt-out</Chip>}
      </div>
      {card.value != null && !cardFields.some((f) => f.key === valueKey) && (
        <div className={classes.kv}>
          <span>Valor</span>
          <span className={classes.kvv}>{money(card.value)}</span>
        </div>
      )}
      {cardFields.map((f) =>
        (contact.attributes || {})[f.key] != null ? (
          <div key={f.key} className={classes.kv}>
            <span>{f.label}</span>
            <span className={classes.kvv}>
              {fmtField(f.type, contact.attributes[f.key])}
            </span>
          </div>
        ) : null,
      )}
      <div className={classes.ft}>
        <span>
          {card.closedAt
            ? `encerrado ${timeAgo(card.closedAt)}`
            : `entrou ${timeAgo(card.enteredStageAt)}`}
        </span>
        {late ? (
          <Chip kind="warn">SLA {slaDays}d</Chip>
        ) : (
          <span>
            {contact.lastInboundAt
              ? `respondeu ${timeAgo(contact.lastInboundAt)}`
              : "sem resposta"}
          </span>
        )}
      </div>
    </>
  );
};

const SortableCard = ({ card, onOpen, ...rest }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `card-${card.id}`,
    data: { type: "card", stageId: card.stageId },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={rest.classes.card}
      {...attributes}
      {...listeners}
      onClick={() => onOpen(card)}
      onKeyDown={(e) =>
        e.key === "Enter"
          ? onOpen(card)
          : listeners && listeners.onKeyDown && listeners.onKeyDown(e)
      }
    >
      <CardBody card={card} {...rest} />
    </div>
  );
};

const ruleText = (rule, stageName) => {
  const to = stageName(rule.toStageId) || "?";
  if (rule.when === "replied") return `Respondeu → ${to}`;
  if (rule.when === "inactive") return `Sem atividade ${rule.days}d → ${to}`;
  if (rule.when === "ai_result")
    return `IA: ${rule.field} ${rule.op === "eq" ? "=" : rule.op} ${rule.value} → ${to}`;
  return "";
};

// nome editável no próprio lugar (clique → digita → Enter)
const InlineName = ({ value, classes, onSave, editing, setEditing }) => {
  const cancelled = useRef(false);
  if (!editing)
    return (
      <span
        className={`${classes.chName} ${classes.chNameEdit}`}
        title="Clique para renomear"
        onClick={() => setEditing(true)}
      >
        {value}
      </span>
    );
  return (
    <input
      className={classes.nameInput}
      autoFocus
      defaultValue={value}
      onFocus={(e) => {
        cancelled.current = false;
        e.target.select();
      }}
      onBlur={(e) => {
        setEditing(false);
        const v = e.target.value.trim();
        if (!cancelled.current && v && v !== value) onSave(v);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.target.blur();
        if (e.key === "Escape") {
          cancelled.current = true;
          e.target.blur();
        }
      }}
    />
  );
};

const Column = ({
  col,
  cards,
  classes,
  onOpen,
  onAdd,
  cardFields,
  valueKey,
  stageName,
  onConfig,
  onMenu,
  onRename,
  editing,
  setEditing,
  isOver,
}) => {
  const { setNodeRef } = useDroppable({
    id: `col-${col.id}`,
    data: { type: "col", stageId: col.id },
  });
  const automated =
    (col.rules || []).length > 0 || (col.automations || []).length > 0;
  return (
    <div className={`${classes.col} ${isOver ? classes.colOver : ""}`}>
      <div className={classes.ch}>
        <div className={classes.chr}>
          <Dot color={col.color} />
          <InlineName
            value={col.name}
            classes={classes}
            onSave={(v) => onRename(col, v)}
            editing={editing}
            setEditing={setEditing}
          />
          {col.kind !== "aberta" && (
            <span
              className={classes.kindTag}
              title="Cards que chegam aqui são encerrados"
            >
              {col.kind === "ganho" ? "ganho" : "perdido"}
            </span>
          )}
          <span className={classes.cnt}>{col.total}</span>
          <IconButton
            size="small"
            className={`${classes.iconBtn} ${automated ? classes.boltOn : ""}`}
            title={
              automated ? "Automação desta etapa" : "Automatizar esta etapa"
            }
            aria-label="Automatizar esta etapa"
            onClick={onConfig}
          >
            <BoltIcon />
          </IconButton>
          <IconButton
            size="small"
            className={classes.iconBtn}
            aria-label="Opções da etapa"
            onClick={(e) => onMenu(e.currentTarget, col)}
          >
            <MoreIcon />
          </IconButton>
        </div>
        {col.sum > 0 && <span className={classes.sum}>{money(col.sum)}</span>}
        {(col.automations || []).map((a) => (
          <span
            key={a.id}
            className={classes.auto}
            title={`Ao entrar: ${a.name}${a.active ? "" : " (desativada)"}`}
            onClick={onConfig}
          >
            <span role="img" aria-label="automação">
              ⚡
            </span>{" "}
            Ao entrar:{" "}
            <b
              style={{
                color: T.ink,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {a.name.split(" — ").pop()}
            </b>
          </span>
        ))}
        {(col.rules || []).map((r, i) => (
          <span
            key={i}
            className={classes.auto}
            onClick={onConfig}
            title="Regra automática da etapa"
          >
            <span role="img" aria-label="regra">
              ↺
            </span>{" "}
            {ruleText(r, stageName)}
          </span>
        ))}
      </div>
      <SortableContext
        items={cards.map((c) => `card-${c.id}`)}
        strategy={verticalListSortingStrategy}
      >
        <div ref={setNodeRef} className={classes.list}>
          {cards.map((card) => (
            <SortableCard
              key={card.id}
              card={card}
              onOpen={onOpen}
              classes={classes}
              cardFields={cardFields}
              valueKey={valueKey}
              slaDays={col.slaDays}
            />
          ))}
          {col.total > cards.length && (
            <div className={classes.more}>
              + {col.total - cards.length} cards (use a busca ou um segmento)
            </div>
          )}
          <button
            type="button"
            className={classes.add}
            onClick={() => onAdd(col)}
          >
            + adicionar
          </button>
        </div>
      </SortableContext>
    </div>
  );
};

// ---------- página ----------
const CrmBoard = () => {
  const classes = useStyles();
  const menu = useMenuStyles();
  const history = useHistory();
  const location = useLocation();
  const { activeProjectId } = useProject();
  const { confirm } = useConfirm();

  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState(
    () => new URLSearchParams(location.search).get("pipelineId") || "",
  );
  const [segments, setSegments] = useState([]);
  const [segmentId, setSegmentId] = useState("");
  const [q, setQ] = useState("");
  const [board, setBoard] = useState(null);
  const [cols, setCols] = useState({}); // stageId → [card]
  const [drag, setDrag] = useState(null); // card arrastado
  const [overCol, setOverCol] = useState(null);
  const [drawer, setDrawer] = useState(null); // contactId
  const [autoStageId, setAutoStageId] = useState(null); // painel ⚡ da etapa
  const [stageMenu, setStageMenu] = useState(null); // { anchor, col }
  const [editingStage, setEditingStage] = useState(null); // id em renomeação
  const [addingStage, setAddingStage] = useState(false);
  const [pipeMenu, setPipeMenu] = useState(null);
  const [settings, setSettings] = useState(null); // { name, valueField, allowMultipleCards }
  const [moneyFields, setMoneyFields] = useState([]);
  const [newPipe, setNewPipe] = useState(null); // { name, template }
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [newCard, setNewCard] = useState(null); // { stageId }
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    value: "",
  });
  const dragging = useRef(false);
  const boardRef = useRef(null);
  const fromStage = useRef(null);

  const loadPipelines = useCallback(async () => {
    if (!activeProjectId) return;
    try {
      const { data } = await api.get("/crm/pipelines", {
        params: { projectId: activeProjectId },
      });
      const list = data.pipelines || [];
      setPipelines(list);
      setPipelineId((cur) =>
        cur && list.some((p) => String(p.id) === String(cur))
          ? cur
          : list[0]
            ? String(list[0].id)
            : "",
      );
    } catch (err) {
      toastError(err);
    }
  }, [activeProjectId]);

  useEffect(() => {
    loadPipelines();
    (async () => {
      if (!activeProjectId) return;
      try {
        const { data } = await api.get("/crm/segments", {
          params: { projectId: activeProjectId },
        });
        setSegments(data.segments || []);
      } catch {
        /* filtro é opcional */
      }
    })();
  }, [loadPipelines, activeProjectId]);

  const loadBoard = useCallback(async () => {
    if (!pipelineId) return;
    try {
      const { data } = await api.get(`/crm/pipelines/${pipelineId}/board`, {
        params: { q: q || undefined, segmentId: segmentId || undefined },
      });
      if (dragging.current) return; // não reordena embaixo do dedo
      setBoard(data);
      const next = {};
      (data.columns || []).forEach((c) => {
        next[c.id] = c.cards;
      });
      setCols(next);
    } catch (err) {
      toastError(err);
    }
  }, [pipelineId, q, segmentId]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  useEffect(() => {
    if (boardRef.current) boardRef.current.scrollLeft = 0; // funil novo começa do início
    if (pipelineId) history.replace({ search: `?pipelineId=${pipelineId}` });
  }, [pipelineId]); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useDebounced(loadBoard, 500);
  useCrmSocket((p) => {
    if (p.kind === "campaign") return;
    if (
      !p.pipelineId ||
      String(p.pipelineId) === String(pipelineId) ||
      p.kind === "contact" ||
      p.kind === "activity" ||
      p.kind === "reconnect"
    )
      reload();
  });

  const searchDebounced = useDebounced((v) => setQ(v), 400);

  const stageName = useCallback(
    (id) =>
      (((board && board.columns) || []).find((c) => c.id === Number(id)) || {})
        .name,
    [board],
  );

  // ---------- drag-and-drop ----------
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const findStage = (id) => {
    if (!id) return null;
    const s = String(id);
    if (s.startsWith("col-")) return Number(s.slice(4));
    const cid = Number(s.replace("card-", ""));
    const k = Object.keys(cols).find((st) =>
      (cols[st] || []).some((c) => c.id === cid),
    );
    return k ? Number(k) : null;
  };

  const onDragStart = ({ active }) => {
    dragging.current = true;
    const st = findStage(active.id);
    fromStage.current = st;
    const cid = Number(String(active.id).replace("card-", ""));
    setDrag((cols[st] || []).find((c) => c.id === cid) || null);
  };

  const onDragOver = ({ active, over }) => {
    if (!over) return;
    const from = findStage(active.id);
    const to = findStage(over.id);
    setOverCol(to);
    if (from == null || to == null || from === to) return;
    const cid = Number(String(active.id).replace("card-", ""));
    setCols((prev) => {
      const card = (prev[from] || []).find((c) => c.id === cid);
      if (!card) return prev;
      const dest = (prev[to] || []).slice();
      const overIdx = String(over.id).startsWith("card-")
        ? dest.findIndex((c) => `card-${c.id}` === String(over.id))
        : dest.length;
      dest.splice(overIdx < 0 ? dest.length : overIdx, 0, {
        ...card,
        stageId: to,
      });
      return {
        ...prev,
        [from]: prev[from].filter((c) => c.id !== cid),
        [to]: dest,
      };
    });
  };

  const onDragEnd = async ({ active, over }) => {
    setOverCol(null);
    setDrag(null);
    const cid = Number(String(active.id).replace("card-", ""));
    const to = over ? findStage(over.id) : null;
    if (to == null) {
      dragging.current = false;
      loadBoard();
      return;
    }
    // ordem final dentro da coluna de destino
    let list = (cols[to] || []).slice();
    const oldIdx = list.findIndex((c) => c.id === cid);
    if (
      String(over.id).startsWith("card-") &&
      String(over.id) !== String(active.id)
    ) {
      const newIdx = list.findIndex((c) => `card-${c.id}` === String(over.id));
      if (oldIdx >= 0 && newIdx >= 0) {
        const [it] = list.splice(oldIdx, 1);
        list.splice(newIdx, 0, it);
      }
    }
    setCols((prev) => ({ ...prev, [to]: list }));
    const idx = list.findIndex((c) => c.id === cid);
    const afterId = idx > 0 ? list[idx - 1].id : null; // card de cima
    const beforeId =
      idx >= 0 && idx < list.length - 1 ? list[idx + 1].id : null; // card de baixo
    const changedStage = fromStage.current !== to;
    try {
      await api.post(`/crm/cards/${cid}/move`, {
        toStageId: to,
        afterId,
        beforeId,
      });
      if (changedStage) {
        const col = (board.columns || []).find((c) => c.id === to);
        if (col && (col.automations || []).length)
          toast.info(
            `"${col.name}": ${col.automations.length === 1 ? "automação disparada" : `${col.automations.length} automações disparadas`}`,
          );
      }
    } catch (err) {
      toastError(err);
    }
    dragging.current = false;
    loadBoard();
  };

  // ---------- ações ----------
  const newPipeline = () => setNewPipe({ name: "", template: "vendas_ia" });

  const createPipeline = async () => {
    try {
      const { data } = await api.post("/crm/pipelines", {
        projectId: activeProjectId,
        name:
          newPipe.name.trim() ||
          TEMPLATES.find((t) => t.value === newPipe.template).label,
        template: newPipe.template,
      });
      setNewPipe(null);
      await loadPipelines();
      setPipelineId(String(data.pipeline.id));
    } catch (err) {
      toastError(err);
    }
  };

  // ---------- etapas, direto no quadro ----------
  const refresh = async () => {
    await loadPipelines();
    loadBoard();
  };
  // lista atual SEM regras (o servidor preserva as regras de quem não manda `rules`)
  const stageList = () =>
    ((board && board.columns) || []).map((c) => ({
      id: c.id,
      name: c.name,
      color: c.color,
      kind: c.kind,
      slaDays: c.slaDays,
    }));
  const saveList = async (list, moveCardsTo) => {
    try {
      await api.put(`/crm/pipelines/${pipelineId}/stages`, {
        stages: list,
        moveCardsTo,
      });
      await refresh();
    } catch (err) {
      toastError(err);
    }
  };
  const patchStage = async (col, patch) => {
    try {
      await api.patch(`/crm/stages/${col.id}`, patch);
      await refresh();
    } catch (err) {
      toastError(err);
    }
  };
  const moveStage = (col, dir) => {
    const list = stageList();
    const i = list.findIndex((s) => s.id === col.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return;
    const [it] = list.splice(i, 1);
    list.splice(j, 0, it);
    saveList(list);
  };
  const deleteStage = async (col) => {
    const list = stageList();
    if (list.length <= 1) return;
    const rest = list.filter((s) => s.id !== col.id);
    const dest = rest.find((s) => s.kind === "aberta") || rest[0];
    const ok = await confirm({
      title: `Excluir a etapa "${col.name}"?`,
      message: col.total
        ? `Os ${col.total} card(s) dela vão para "${dest.name}".`
        : "A etapa está vazia.",
      confirmText: "Excluir",
      danger: true,
    });
    if (ok) saveList(rest, dest.id);
  };
  const addStage = (name) => {
    setAddingStage(false);
    if (!name) return;
    const list = stageList();
    const used = new Set(list.map((s) => s.color));
    const color =
      STAGE_COLORS.find((c) => !used.has(c)) ||
      STAGE_COLORS[list.length % STAGE_COLORS.length];
    // entra antes das etapas finais (ganho/perdido)
    const at = list.findIndex((s) => s.kind !== "aberta");
    list.splice(at < 0 ? list.length : at, 0, { name, color, kind: "aberta" });
    saveList(list);
  };

  const openSettings = async () => {
    setPipeMenu(null);
    setSettings({
      name: pipeline.name,
      valueField: pipeline.valueField || "",
      allowMultipleCards: !!pipeline.allowMultipleCards,
    });
    try {
      const { data } = await api.get("/crm/fields", {
        params: { projectId: activeProjectId },
      });
      setMoneyFields(
        (data.fields || []).filter(
          (f) => f.type === "moeda" || f.type === "numero",
        ),
      );
    } catch {
      setMoneyFields([]);
    }
  };
  const saveSettings = async () => {
    try {
      await api.patch(`/crm/pipelines/${pipelineId}`, {
        name: settings.name,
        valueField: settings.valueField || null,
        allowMultipleCards: settings.allowMultipleCards,
      });
      setSettings(null);
      await refresh();
    } catch (err) {
      toastError(err);
    }
  };
  const deletePipeline = async () => {
    setPipeMenu(null);
    const ok = await confirm({
      title: `Excluir o funil "${pipeline.name}"?`,
      message: "Os cards deste funil somem. Os contatos continuam no CRM.",
      confirmText: "Excluir funil",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/crm/pipelines/${pipelineId}`);
      setPipelineId("");
      await loadPipelines();
    } catch (err) {
      toastError(err);
    }
  };

  const openAdd = (col) => {
    setForm({ name: "", phone: "", email: "", value: "" });
    setNewCard({
      stageId: col
        ? col.id
        : (board && board.columns[0] && board.columns[0].id) || null,
    });
  };

  const saveCard = async () => {
    try {
      await api.post("/crm/cards", {
        pipelineId,
        stageId: newCard.stageId,
        value: form.value
          ? Number(String(form.value).replace(/\./g, "").replace(",", "."))
          : null,
        contact: { name: form.name, phone: form.phone, email: form.email },
      });
      setNewCard(null);
      loadBoard();
    } catch (err) {
      toastError(err);
    }
  };

  const pipeline = useMemo(
    () => pipelines.find((p) => String(p.id) === String(pipelineId)) || null,
    [pipelines, pipelineId],
  );
  const columns = (board && board.columns) || [];
  const cardFields = (board && board.cardFields) || [];
  const valueKey = board && board.pipeline && board.pipeline.valueKey;
  const totalCards = columns.reduce((s, c) => s + c.total, 0);

  return (
    <div className={classes.page}>
      <div className={classes.top}>
        <div className={classes.headRow}>
          <div className={classes.headIcon}>
            <FunnelIcon />
          </div>
          <span className={classes.title}>Funis</span>
          <span className={classes.grow} />
          <div className={classes.actions}>
            <Button
              className={classes.btn}
              onClick={() => setSourcesOpen(true)}
            >
              Fontes de dados
            </Button>
            <Button
              className={classes.btn}
              onClick={() =>
                history.push(`/crm/report?pipelineId=${pipelineId}`)
              }
              disabled={!pipeline}
            >
              Relatório
            </Button>
            <Button
              className={classes.btnPri}
              startIcon={<AddIcon />}
              onClick={() => openAdd(null)}
              disabled={!pipeline}
            >
              Novo contato
            </Button>
          </div>
        </div>
        <div className={classes.tools}>
          <SelectField
            fullWidth={false}
            className={classes.sel}
            value={pipelineId}
            onChange={(v) =>
              v === "__new__" ? newPipeline() : setPipelineId(String(v))
            }
            options={[
              ...pipelines.map((p) => ({ value: String(p.id), label: p.name })),
              { value: "__new__", label: "+ Novo funil…" },
            ]}
          />
          <IconButton
            size="small"
            aria-label="Opções do funil"
            onClick={(e) => setPipeMenu(e.currentTarget)}
            disabled={!pipeline}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <SelectField
            fullWidth={false}
            className={classes.sel}
            value={segmentId}
            onChange={(v) => setSegmentId(v)}
            emptyLabel="Todos os contatos"
            options={segments.map((s) => ({
              value: String(s.id),
              label: s.name,
            }))}
          />
          <TextField
            className={classes.search}
            size="small"
            variant="outlined"
            placeholder="Buscar nome, telefone…"
            onChange={(e) => searchDebounced(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 17, color: T.ink3 }} />
                </InputAdornment>
              ),
            }}
          />
          <span className={classes.grow} />
          <Chip kind="ok">
            <Dot color={T.ok} size={6} /> ao vivo
          </Chip>
        </div>
      </div>

      {board && !totalCards && !q && !segmentId && (
        <div className={classes.banner}>
          <span>
            <b>Funil vazio.</b> Conecte um coletor ou o WhatsApp e os contatos
            caem aqui sozinhos.
          </span>
          <Button
            className={classes.btnPri}
            onClick={() => setSourcesOpen(true)}
          >
            Conectar uma fonte
          </Button>
        </div>
      )}
      <div className={classes.board} ref={boardRef}>
        {
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            onDragCancel={() => {
              dragging.current = false;
              setDrag(null);
              setOverCol(null);
              loadBoard();
            }}
          >
            {columns.map((col) => (
              <Column
                key={col.id}
                col={col}
                cards={cols[col.id] || []}
                classes={classes}
                onOpen={(c) => setDrawer(c.contact && c.contact.id)}
                onAdd={openAdd}
                cardFields={cardFields}
                valueKey={valueKey}
                stageName={stageName}
                onConfig={() => setAutoStageId(col.id)}
                onMenu={(anchor, c) => setStageMenu({ anchor, col: c })}
                onRename={(c, name) => patchStage(c, { name })}
                editing={editingStage === col.id}
                setEditing={(on) => setEditingStage(on ? col.id : null)}
                isOver={drag && overCol === col.id && drag.stageId !== col.id}
              />
            ))}
            <div className={classes.ghost}>
              {addingStage ? (
                <input
                  className={classes.nameInput}
                  autoFocus
                  placeholder="Nome da etapa"
                  onBlur={(e) => addStage(e.target.value.trim())}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.target.blur();
                    if (e.key === "Escape") {
                      e.target.value = "";
                      e.target.blur();
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  className={classes.ghostBtn}
                  onClick={() => setAddingStage(true)}
                >
                  + Nova etapa
                </button>
              )}
            </div>
            <DragOverlay>
              {drag ? (
                <div
                  className={`${classes.card} ${classes.cardDrag}`}
                  style={{ width: COL_W - 16 }}
                >
                  <CardBody
                    card={drag}
                    classes={classes}
                    cardFields={cardFields}
                    valueKey={valueKey}
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        }
      </div>

      <ContactDrawer
        open={!!drawer}
        contactId={drawer}
        onClose={() => setDrawer(null)}
        onChanged={loadBoard}
      />
      <StageAutomations
        open={!!autoStageId}
        onClose={() => setAutoStageId(null)}
        stage={columns.find((c) => c.id === autoStageId) || null}
        columns={columns}
        projectId={activeProjectId}
        onChanged={loadBoard}
      />

      <Menu
        anchorEl={stageMenu && stageMenu.anchor}
        open={!!stageMenu}
        onClose={() => setStageMenu(null)}
        PaperProps={{ className: menu.paper }}
        MenuListProps={{ disablePadding: true }}
      >
        {stageMenu && [
          <MenuItem
            key="auto"
            className={menu.item}
            onClick={() => {
              setAutoStageId(stageMenu.col.id);
              setStageMenu(null);
            }}
          >
            <BoltIcon /> Automatizar
          </MenuItem>,
          <MenuItem
            key="ren"
            className={menu.item}
            onClick={() => {
              setEditingStage(stageMenu.col.id);
              setStageMenu(null);
            }}
          >
            <EditIcon /> Renomear
          </MenuItem>,
          <div key="cor" className={classes.swRow}>
            {STAGE_COLORS.map((cl) => (
              <button
                type="button"
                key={cl}
                aria-label={`cor ${cl}`}
                className={`${classes.sw} ${stageMenu.col.color === cl ? classes.swOn : ""}`}
                style={{ background: cl }}
                onClick={() => {
                  patchStage(stageMenu.col, { color: cl });
                  setStageMenu(null);
                }}
              />
            ))}
          </div>,
          <div key="d1" className={menu.divider} />,
          <MenuItem
            key="l"
            className={menu.item}
            disabled={columns[0] && columns[0].id === stageMenu.col.id}
            onClick={() => {
              moveStage(stageMenu.col, -1);
              setStageMenu(null);
            }}
          >
            <LeftIcon /> Mover para a esquerda
          </MenuItem>,
          <MenuItem
            key="r"
            className={menu.item}
            disabled={
              columns.length &&
              columns[columns.length - 1].id === stageMenu.col.id
            }
            onClick={() => {
              moveStage(stageMenu.col, 1);
              setStageMenu(null);
            }}
          >
            <RightIcon /> Mover para a direita
          </MenuItem>,
          <div key="d2" className={menu.divider} />,
          <div key="lbl" className={menu.sectionLabel}>
            Card que chega aqui
          </div>,
          ...[
            { k: "aberta", l: "Continua em andamento" },
            { k: "ganho", l: "Fecha como ganho" },
            { k: "perdido", l: "Fecha como perdido" },
          ].map((o) => (
            <MenuItem
              key={o.k}
              className={menu.item}
              onClick={() => {
                if (stageMenu.col.kind !== o.k)
                  patchStage(stageMenu.col, { kind: o.k });
                setStageMenu(null);
              }}
            >
              {stageMenu.col.kind === o.k ? (
                <CheckIcon />
              ) : (
                <span style={{ width: 18 }} />
              )}{" "}
              {o.l}
            </MenuItem>
          )),
          <div key="d3" className={menu.divider} />,
          <MenuItem
            key="del"
            className={`${menu.item} ${menu.danger}`}
            disabled={columns.length <= 1}
            onClick={() => {
              const col = stageMenu.col;
              setStageMenu(null);
              deleteStage(col);
            }}
          >
            <DeleteIcon /> Excluir etapa
          </MenuItem>,
        ]}
      </Menu>

      <Menu
        anchorEl={pipeMenu}
        open={!!pipeMenu}
        onClose={() => setPipeMenu(null)}
        PaperProps={{ className: menu.paper }}
        MenuListProps={{ disablePadding: true }}
      >
        <MenuItem className={menu.item} onClick={openSettings}>
          <SettingsIcon /> Configurações do funil
        </MenuItem>
        <MenuItem
          className={menu.item}
          onClick={() => {
            setPipeMenu(null);
            newPipeline();
          }}
        >
          <AddIcon /> Novo funil
        </MenuItem>
        <div className={menu.divider} />
        <MenuItem
          className={`${menu.item} ${menu.danger}`}
          onClick={deletePipeline}
          disabled={pipelines.length <= 1}
        >
          <DeleteIcon /> Excluir funil
        </MenuItem>
      </Menu>

      <Dialog
        open={!!settings}
        onClose={() => setSettings(null)}
        PaperProps={{ className: menu.dialogPaper }}
      >
        {settings && (
          <div className={classes.dlg}>
            <h3 className={classes.dlgTitle}>Configurações do funil</h3>
            <TextField
              label="Nome"
              value={settings.name}
              onChange={(e) => {
                const { value } = e.target;
                setSettings((x) => ({ ...x, name: value }));
              }}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <SelectField
              label="Valor mostrado no card"
              value={settings.valueField}
              onChange={(v) => setSettings((x) => ({ ...x, valueField: v }))}
              emptyLabel="Automático (o primeiro campo em R$)"
              options={moneyFields.map((f) => ({
                value: f.key,
                label: f.label,
              }))}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  color="default"
                  checked={settings.allowMultipleCards}
                  onChange={(e) => {
                    const { checked } = e.target;
                    setSettings((x) => ({ ...x, allowMultipleCards: checked }));
                  }}
                />
              }
              label={
                <span style={{ fontSize: 12.5 }}>
                  Um card por pedido (o mesmo cliente pode aparecer mais de uma
                  vez)
                </span>
              }
            />
            <div className={classes.row}>
              <Button className={classes.btn} onClick={() => setSettings(null)}>
                Cancelar
              </Button>
              <Button className={classes.btnPri} onClick={saveSettings}>
                Salvar
              </Button>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        open={!!newPipe}
        onClose={() => setNewPipe(null)}
        PaperProps={{ className: menu.dialogPaper }}
      >
        {newPipe && (
          <div className={classes.dlg} style={{ width: 480 }}>
            <h3 className={classes.dlgTitle}>Novo funil</h3>
            {TEMPLATES.map((t) => (
              <button
                type="button"
                key={t.value}
                className={`${classes.tpl} ${newPipe.template === t.value ? classes.tplOn : ""}`}
                onClick={() => setNewPipe((x) => ({ ...x, template: t.value }))}
              >
                <span className={classes.tplName}>{t.label}</span>
                <span className={classes.tplSub}>{t.sub}</span>
              </button>
            ))}
            <TextField
              label="Nome (opcional)"
              value={newPipe.name}
              onChange={(e) => {
                const { value } = e.target;
                setNewPipe((x) => ({ ...x, name: value }));
              }}
              placeholder={
                TEMPLATES.find((t) => t.value === newPipe.template).label
              }
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <span style={{ fontSize: 11.5, color: T.ink3 }}>
              Depois é só mexer no quadro: clique no nome da etapa para renomear
              e no{" "}
              <span role="img" aria-label="raio">
                ⚡
              </span>{" "}
              para automatizar.
            </span>
            <div className={classes.row}>
              <Button className={classes.btn} onClick={() => setNewPipe(null)}>
                Cancelar
              </Button>
              <Button className={classes.btnPri} onClick={createPipeline}>
                Criar funil
              </Button>
            </div>
          </div>
        )}
      </Dialog>
      <SourcesDialog
        open={sourcesOpen}
        onClose={() => setSourcesOpen(false)}
        projectId={activeProjectId}
        pipelines={pipelines}
        onDone={() => {
          loadPipelines();
          loadBoard();
        }}
      />

      <Dialog
        open={!!newCard}
        onClose={() => setNewCard(null)}
        PaperProps={{ className: menu.dialogPaper }}
      >
        <div className={classes.dlg}>
          <h3 className={classes.dlgTitle}>Adicionar contato ao funil</h3>
          <TextField
            label="Nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            autoFocus
          />
          <TextField
            label="Telefone (WhatsApp)"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            placeholder="(19) 99999-0000"
          />
          <TextField
            label="E-mail"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Valor (opcional)"
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            placeholder="R$"
          />
          <SelectField
            label="Etapa"
            value={newCard ? newCard.stageId : ""}
            onChange={(v) => setNewCard({ stageId: v })}
            options={columns.map((c) => ({ value: c.id, label: c.name }))}
          />
          <span style={{ fontSize: 11.5, color: T.ink3 }}>
            Se o telefone ou e-mail já existir no CRM, o contato é o mesmo — não
            duplica.
          </span>
          <div className={classes.row}>
            <Button className={classes.btn} onClick={() => setNewCard(null)}>
              Cancelar
            </Button>
            <Button
              className={classes.btnPri}
              onClick={saveCard}
              disabled={!form.phone.trim() && !form.email.trim()}
            >
              Adicionar
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default CrmBoard;
